import DataLayer from '@services/data-layer/index'
import { getCookie, deleteCookie } from '@utils/cookies'
import localSave from '@utils/local-save'
import postAuthRedirect from '@utils/post-auth-redirect'
import analyticsService from './analytics-service'
import settingsService from './settings-service'
import consentService from './consent-service'
import { onDemandQualityOptions } from '../configuration'
import { useChannelsStore } from '@state/stores/channels'
import { useAppearanceStore } from '@state/stores/appearance'
import { useAuthStore } from '@state/stores/auth'
import { useFavoritesStore } from '@state/stores/favorites'
import { useGenresStore } from '@state/stores/genres'
import { useMemberStore } from '@state/stores/member'
import { usePremiumStore } from '@state/stores/premium'
import { useSettingsStore } from '@state/stores/settings'

const CHECK_VERSION_INTERVAL_DURATION = 1000 * 60 * 60 // 1 hour

class AppInitializer {
    constructor({ router, messages, features = {} }) {
        this.router = router
        this.messages = messages
        this.checkVersionInterval = null

        this.features = {
            auth: true,
            channels: true,
            genres: true,
            versionCheck: true,
            premium: true,
            settings: true,
            favouritesOnboarding: true,
            premiumOnboarding: true,
            ...features,
        }
    }

    isFeatureEnabled(key) {
        return !!this.features[key]
    }

    enableFeature(key) {
        this.features[key] = true
    }

    disableFeature(key) {
        this.features[key] = false

        if (key === 'versionCheck' && this.checkVersionInterval) {
            clearInterval(this.checkVersionInterval)
            this.checkVersionInterval = null
        }
    }

    setFeatures(newFeatures = {}) {
        this.features = Object.assign(this.features, newFeatures)
    }

    get isLoggedIn() {
        return useAuthStore().loggedIn
    }

    get isPremium() {
        return usePremiumStore().isPremium
    }

    get currentUser() {
        return useAuthStore().currentUser?.members_id
    }

    async initialize() {
        if (this.isFeatureEnabled('auth')) await this._runStep('auth', () => this.checkAuthentication())
        if (this.isFeatureEnabled('channels')) this.initChannels()
        if (this.isFeatureEnabled('genres')) this.initGenres()
        if (this.isFeatureEnabled('versionCheck')) this.startCodeVersionCheck()
        if (this.isFeatureEnabled('premium')) await this._runStep('premium', () => this.initPremium())
        if (this.isFeatureEnabled('settings')) await this._runStep('settings', () => this.initSettings())
        if (this.isFeatureEnabled('favouritesOnboarding')) return this._runStep('favouritesOnboarding', () => this.initOnboarding())
        if (this.isFeatureEnabled('premiumOnboarding')) await this._runStep('premiumOnboarding', () => this.handlePremiumOnboarding())
    }

    // A failed boot step must degrade, never strand the splash screen. A 401
    // with a persisted session means the session is dead server-side — hand it
    // to auth/expireSession; anything else is logged and skipped.
    async _runStep(name, step) {
        try {
            return await step()
        } catch (error) {
            if (error?.response?.status === 401 && this.isLoggedIn) {
                try {
                    return await useAuthStore().expireSession()
                } catch (expireError) {
                    console.error('Session expiry failed:', expireError)
                    return null
                }
            }
            console.error(`Boot step '${name}' failed:`, error)
            return null
        }
    }

    // Authentication Methods
    checkAuthentication() {
        if (!this.isFeatureEnabled('auth')) return

        const oauthSession = getCookie('oauthSession') || ''
        if (!oauthSession) return

        // The cookie is single-use: drop it even when the payload is malformed.
        // The parse error then propagates to _runStep and boot continues logged out.
        let user
        try {
            user = JSON.parse(oauthSession)
        } finally {
            deleteCookie({ name: 'oauthSession', domain: import.meta.env.VITE_COOKIES_DOMAIN })
        }
        useAuthStore().setUser(user)
        useMemberStore().init(user)

        // Grant consent for OAuth logins (user agreed to Privacy Policy by using social auth)
        consentService.grantConsent('oauth')

        postAuthRedirect.resolveAfterAuth(localSave.get('redirectURL') || '/')
    }

    // Channel Initialization
    initChannels() {
        if (!this.isFeatureEnabled('channels')) return

        DataLayer.channels.get().then(response => {
            const channels = Object.values(response.body).sort((a, b) =>
                a.title > b.title ? 1 : b.title > a.title ? -1 : 0
            )
            useChannelsStore().setChannels(channels)
        })
    }

    // Genre Initialization
    async initGenres() {
        if (!this.isFeatureEnabled('genres')) return

        const genresRes = await DataLayer.genres.getAll()

        const genres = genresRes ? genresRes.body : []

        useGenresStore().setGenres(genres)
    }

    // Code Version Checking
    startCodeVersionCheck() {
        if (!this.isFeatureEnabled('versionCheck')) return

        this.checkCodeVersion()
        this.checkVersionInterval = setInterval(() => {
            this.checkCodeVersion()
        }, CHECK_VERSION_INTERVAL_DURATION)
    }

    async checkCodeVersion() {
        if (!this.isFeatureEnabled('versionCheck')) return

        const localCodeVersion = localSave.get('localCodeVersion')
        try {
            const response = await DataLayer.versions.get()
            const actualCodeVersion = response.body.clientVersion
            localSave.save('actualCodeVersion', actualCodeVersion)

            if (!localCodeVersion) localSave.save('localCodeVersion', actualCodeVersion)
            else if (localCodeVersion !== actualCodeVersion) {
                this.notifyForNewVersion(localCodeVersion, response.body)
            }
        } catch (error) {
            console.error('Failed to check code version:', error)
        }
    }

    notifyForNewVersion(oldVersion, newVersion) {
        const messageKey = newVersion.severity === 'critical' ? 'CRITICAL_UPDATE_AVAILABLE' : 'UPDATE_AVAILABLE'

        useAppearanceStore().handleNotification({
            message: this.messages(messageKey),
            type: 'warning',
            hasReload: true,
            isPersistentNotification: true,
            forceUpdate: newVersion.severity === 'critical',
        })
    }

    async handlePremiumLoad() {
        if (!this.isFeatureEnabled('premium')) return
        if (!this.isLoggedIn) return
        if (!localSave.get('hasAppLoaded') && !this.isPremium) {
            localSave.save('hasAppLoaded', true)
            if (window.location.pathname.includes('premium')) return
            analyticsService.premiumUpsellInitiated('non-premium-log-in')
            this.router.push({ path: '/premium' })
        }
    }

    // Premium Initialization
    async initPremium() {
        if (!this.isFeatureEnabled('premium')) return

        const premiumStore = usePremiumStore()

        try {
            const premiumService = DataLayer.subscriptions
            const promises = [premiumService.getProducts()]
            const memberId = this.currentUser?.id
            if (this.isLoggedIn) promises.push(premiumService.getSubscriptionsByMemberId(memberId))
            const [plans, subscriptions = null] = await Promise.all(promises)
            premiumStore.setPlans(plans.body)
            if (subscriptions) {
                premiumStore.setSubscriptions(subscriptions.body)
                const activeSubscription = premiumStore.activeSubscription
                if (activeSubscription?.subscriptionsCheckoutId?.id) {
                    try {
                        const transaction = await premiumService.getCheckoutById(activeSubscription.subscriptionsCheckoutId.id)
                        premiumStore.setActiveTransaction(transaction.body)
                    } catch (error) {
                        // The checkout only feeds payment-method display on the
                        // account screens — a broken record must not block boot.
                        console.error('Failed to load the active checkout:', error)
                    }
                }
            }
            await this.handlePremiumLoad()
        } finally {
            premiumStore.setIsLoading(false)
        }
    }

    async handlePremiumOnboarding() {
        if (!this.isFeatureEnabled('premiumOnboarding')) return
        if (!this.isLoggedIn || localSave.get('hasSkippedPremiumOnboarding') || this.isPremium) return

        // currentRoute is vue-router's START route (name: null) until the initial
        // navigation confirms, which can lose the race against the boot awaits.
        const currentRouteName = this.router.currentRoute.value.name || ''
        if (!currentRouteName.includes('premium')) {
            analyticsService.premiumUpsellInitiated('non-premium-log-in')
            await this.router.push({ path: '/premium' })
        }
    }

    // Settings Initialization
    async initSettings() {
        if (!this.isFeatureEnabled('settings')) return
        if (!this.isLoggedIn) return

        const savedQuality = await settingsService.getCurrentOnDemandQualityKey()
        const defaultQuality = this.isPremium ? 'high' : 'low'
        let onDemandQuality = savedQuality || defaultQuality

        // Validate that saved quality matches premium entitlement
        if (savedQuality) {
            const savedQualityOption = onDemandQualityOptions[savedQuality]

            // If user has a premium quality saved but is no longer premium, reset to default
            if (savedQualityOption?.needsSub && !this.isPremium) {
                onDemandQuality = defaultQuality
                await settingsService.setCurrentOnDemandQuality(onDemandQuality)
            }
        } else {
            // No saved preference, set default
            await settingsService.setCurrentOnDemandQuality(onDemandQuality)
        }

        useSettingsStore().setSettings({ currentOnDemandQualityKey: onDemandQuality })
    }

    // Favorites onboarding
    async initFavorites() {
        if (!this.currentUser) return null

        const favoritesStore = useFavoritesStore()
        await Promise.all([
            favoritesStore.fetchType('artists'),
            favoritesStore.fetchType('genres'),
        ])

        return {
            artists: favoritesStore.artistsList,
            genres: favoritesStore.genresList,
        }
    }

    async initOnboarding() {
        if (!this.isFeatureEnabled('favouritesOnboarding')) return false
        if (!this.currentUser) return false

        const MINIMUM_COUNT = 3
        const ONBOARDING_PATH = 'onboarding'
        const favorites = await this.initFavorites()
        if (!favorites) return false

        const { artists, genres } = favorites
        const shouldOnboard = artists.length < MINIMUM_COUNT && genres.length < MINIMUM_COUNT
        const isOnboarding = this.router.currentRoute.value.name === ONBOARDING_PATH

        if (!shouldOnboard) return false
        else {
            if (isOnboarding) return true
            await this.router.push({ name: ONBOARDING_PATH })
            return true
        }
    }

    // Cleanup
    destroy() {
        if (this.checkVersionInterval) clearInterval(this.checkVersionInterval)
    }
}

export default AppInitializer
